<template>
  <p class="panel-title">{{ $t('app.campaign.newPage.recap.optionsSection.title') }}</p>
  <div v-if="currentCampaign" @keydown.enter="save" tabindex="0" class="options-panel-content">
    <div class="questions">
      <div class="question">
        <label>{{ $t('app.campaign.newPage.slidingPanel.options.locale.label') }}</label>
        <div class="choice">
          <input name="language" value="fr-FR" v-model="currentCampaign.campaign.locale" type="radio">{{ $t('app.campaign.newPage.slidingPanel.options.locale.fr') }}</input>
          <input name="language" value="en-US" v-model="currentCampaign.campaign.locale" type="radio">{{ $t('app.campaign.newPage.slidingPanel.options.locale.en') }}</input>
        </div>
      </div>
      <div class="question" v-if="false">
        <label>{{ $t('app.campaign.newPage.slidingPanel.options.force_locale.label') }}</label>
        <div class="choice">
          <input name="force-language" :value="true" v-model="currentCampaign.campaign.force_locale" type="radio">{{ $t('app.campaign.newPage.slidingPanel.options.yes') }}</input>
          <input name="force-language" :value="false" v-model="currentCampaign.campaign.force_locale" type="radio">{{ $t('app.campaign.newPage.slidingPanel.options.no') }}</input>
        </div>
      </div>
      <div class="question" v-if="false">
        <label>{{ $t('app.campaign.newPage.slidingPanel.options.postGame.label') }}</label>
        <div class="choice">
          <input name="post-game" :value="true" v-model="currentCampaign.surveys.demographic" type="radio">{{ $t('app.campaign.newPage.slidingPanel.options.yes') }}</input>
          <input name="post-game" :value="false" v-model="currentCampaign.surveys.demographic" type="radio">{{ $t('app.campaign.newPage.slidingPanel.options.no') }}</input>
        </div>
      </div>
      <div class="question">
        <label>{{ $t('app.campaign.newPage.slidingPanel.options.report.label') }}</label>
        <div class="choice">
          <input name="talent-report" type="radio" :value="{ send_to_talent: true, format: 'simplified' }" v-model="currentCampaign.documents.report">{{ $t('app.campaign.newPage.slidingPanel.options.report.simplified') }}</input>
          <input name="talent-report" type="radio" :value="{ send_to_talent: true, format: 'complete' }" v-model="currentCampaign.documents.report">{{ $t('app.campaign.newPage.slidingPanel.options.report.complete') }}</input>
          <input name="talent-report" type="radio" :value="{ send_to_talent: false, format: 'simplified' }" v-model="currentCampaign.documents.report">{{ $t('app.campaign.newPage.slidingPanel.options.report.none') }}</input>
        </div>
      </div>
      <div class="question" v-if="false">
        <label>{{ $t('app.campaign.newPage.slidingPanel.options.certificate.label') }}</label>
        <div class="choice">
          <input name="talent-certificate" type="radio" :value="true" v-model="currentCampaign.documents.certification.send_to_talent">{{ $t('app.campaign.newPage.slidingPanel.options.yes') }}</input>
          <input name="talent-certificate" type="radio" :value="false" v-model="currentCampaign.documents.certification.send_to_talent">{{ $t('app.campaign.newPage.slidingPanel.options.no') }}</input>
        </div>
      </div>
    </div>
    <div>
      <div class="section-title">
        <p>{{ $t('app.campaign.newPage.slidingPanel.customization.title') }}</p>
        <Button class="square outline-white" @click="emit('openCustomizationPanel', true)">
          <template #prefix>
            <GearIcon style="width: 18px"></GearIcon>
          </template>
        </Button>
      </div>
      <CampaignCard style="width: 400px !important;" :modelValue="currentCampaign" mode="preview" />
    </div>
  </div>
  <div class="edit-buttons">
    <Button class="secondary" :label="$t('app.campaign.newPage.slidingPanel.buttons.cancel')" @click="emit('close')"></Button>
    <Button class="green" :label="$t('app.campaign.newPage.slidingPanel.buttons.save')" @click="save"></Button>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.panel-title {
  color: var(--secondary-color);
  font-family: $bioSans;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  margin-bottom: 32px;
}

.edit-buttons {
  margin-top: 32px;
  width: 100%;
  display: flex;
  gap: 12px;
  justify-content: end;
}

.options-panel-content {
  display: flex;
  flex-direction: column;
  gap: 32px;

  .section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;

    p {
      color: var(--secondary-color);
      font-family: $bioSans;
      font-size: 20px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    }
  }

  .questions {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .question {
      display: flex;
      flex-direction: column;
      gap: 8px;
  
      label {
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px; /* 150% */
      }
  
      .choice {
        display: flex;
        gap: 8px;
  
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
      }
    }
  }
}
</style>

<script setup lang="ts">
import GearIcon from '@/assets/images/icon/campaign/gear.svg?component';
import CampaignCard from '@/components/global/CampaignCard.vue';

const props = defineProps<{
  campaign : CampaignCreation,
}>();

const currentCampaign: Ref<CampaignCreation> = ref(undefined);


const emit = defineEmits<{
  (e: 'save', value: CampaignCreation): void,
  (e: 'openCustomizationPanel', value: boolean),
  (e: 'close'): void
}>();

function save() {
  emit('save', structuredClone(toRaw(currentCampaign.value)));
  emit('close');
}

onMounted(() => {
  if (props.campaign) {
    currentCampaign.value = structuredClone(toRaw(props.campaign));
  }
});

</script>